// Live scan progress — polls /scan-status every 3s, shows phase breakdown,
// log tail, and a Cancel button that aborts the active task.

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Loading } from "@/components/ui/Loading";
import { Stat } from "@/components/ui/Stat";
import {
  useCancelTask,
  useEngagementScanStatus,
  useEngagementTasks,
} from "@/hooks/useEngagementDetail";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import { confirm } from "@/stores/confirmStore";
import { toast } from "@/stores/toastStore";

export function LiveProgress({ engagementId }: { engagementId: string }) {
  const t = useDict().app.liveProgress;
  const ph = useDict().app.phases;
  const status = useEngagementScanStatus(engagementId);
  const tasks = useEngagementTasks(engagementId, 5_000);
  const cancel = useCancelTask(engagementId);

  if (status.isError) {
    const msg =
      status.error instanceof Error ? status.error.message : t.unknownError;
    return (
      <Card className="border-danger">
        <div className="text-danger font-mono text-[12px]">
          {t.statusError} {msg}
        </div>
      </Card>
    );
  }

  if (!status.data) {
    return (
      <Card>
        <Loading text={t.connecting} />
      </Card>
    );
  }

  const st = status.data;
  const elapsed = st.elapsed_seconds ?? 0;
  const mins = Math.floor(elapsed / 60);
  const secs = Math.floor(elapsed % 60);

  async function handleCancel() {
    const ok = await confirm({
      title: t.cancelTitle,
      message: t.cancelMsg,
      variant: "danger",
      confirmLabel: t.cancelConfirm,
      cancelLabel: t.cancelKeep,
    });
    if (!ok) return;

    const active = tasks.data?.find(
      (t) => t.status === "running" || t.status === "queued",
    );
    if (!active) {
      toast.error(t.noActiveTask);
      return;
    }
    try {
      await cancel.mutateAsync(active.id);
      toast.success(t.cancelled);
    } catch (e) {
      toast.error(`${t.cancelFailed} ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-3 h-3 rounded-full"
          style={{ background: "rgb(var(--color-accent))", animation: "glow 2s infinite" }}
        />
        <div className="font-display text-[16px] font-semibold text-content">
          {t.inProgress}
        </div>
        <div className="flex-1" />
        <div className="font-mono text-[12px] text-muted">
          {fmt(t.elapsed, { m: mins, s: secs })}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <Stat
          label={t.evidenceCollected}
          value={st.evidence_collected}
          color="rgb(var(--color-accent))"
        />
        <Stat
          label={t.stepsCompleted}
          value={`${st.successful_executions}/${st.total_executions}`}
          color="rgb(var(--color-content))"
        />
        <Stat
          label={t.statusLabel}
          value={st.status}
          color={st.is_running ? "rgb(var(--color-accent))" : "rgb(var(--color-success))"}
        />
      </div>

      {st.current_phase && (
        <div
          className="p-3 bg-bg2 rounded mb-3"
          style={{ border: "1px solid rgb(var(--color-accent) / 0.251)" }}
        >
          <div className="font-mono text-[10px] text-muted mb-1">{t.nowRunning}</div>
          <div className="font-display text-[15px] text-accent font-semibold">
            {ph[st.current_phase as keyof typeof ph] ?? st.current_phase}
          </div>
        </div>
      )}

      {st.phases && Object.keys(st.phases).length > 0 && (
        <div>
          <div className="font-mono text-[10px] text-muted uppercase tracking-[0.05em] mb-2">
            {t.phaseProgress}
          </div>
          {Object.entries(st.phases).map(([p, d]) => {
            const pct = d.total > 0 ? (d.completed / d.total) * 100 : 0;
            return (
              <div key={p} className="mb-2">
                <div className="flex justify-between flex-wrap gap-x-3 font-mono text-[11px] mb-[3px]">
                  <span className="text-content">
                    {ph[p as keyof typeof ph] ?? p}
                  </span>
                  <span className="text-muted">
                    {fmt(t.stepsEvidence, { completed: d.completed, total: d.total, evidence: d.evidence })}
                  </span>
                </div>
                <div className="h-1 bg-bg2 rounded-[2px] overflow-hidden">
                  <div
                    className="h-full transition-[width] duration-300"
                    style={{
                      width: `${pct}%`,
                      background: pct === 100 ? "rgb(var(--color-success))" : "rgb(var(--color-accent))",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}


      <div className="mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="font-mono text-[10px] text-dim">
          {t.polling}
        </div>
        <Button variant="danger" small onClick={handleCancel}>
          {t.stopScan}
        </Button>
      </div>
    </Card>
  );
}
